const TZ_MAP: Record<string, string> = {
  'US/Eastern': 'America/New_York',
  'US/Central': 'America/Chicago',
  'US/Mountain': 'America/Denver',
  'US/Pacific': 'America/Los_Angeles',
  'US/Alaska': 'America/Anchorage',
  'US/Hawaii': 'Pacific/Honolulu',
  'US/Arizona': 'America/Phoenix',
};

function resolveTimezone(tz: string): string {
  return TZ_MAP[tz] || tz;
}

/** Map a legacy/config timezone name to an IANA zone usable by Intl. */
export function resolveTimezoneName(tz: string): string {
  return resolveTimezone(tz || 'UTC');
}

// Initialize from localStorage if available (persists across page loads / popups)
let cachedTimezone = resolveTimezone(localStorage.getItem('cad_timezone') || 'UTC');
let tzLoaded = cachedTimezone !== 'UTC';

export function setTimezone(tz: string) {
  const resolved = resolveTimezone(tz || 'UTC');
  cachedTimezone = resolved;
  tzLoaded = true;
  localStorage.setItem('cad_timezone', resolved);
}

export function getTimezone(): string {
  return cachedTimezone;
}

export async function ensureTimezone(): Promise<void> {
  // Always fetch fresh from API to ensure we have the latest
  try {
    const r = await fetch('/api/system-config/public');
    if (r.ok) {
      const data = await r.json();
      if (data.timezone) {
        setTimezone(data.timezone);
        return;
      }
    }
  } catch {
    // ignore
  }
  // If fetch failed but we have a cached value from localStorage, use it
  if (!tzLoaded) {
    const stored = localStorage.getItem('cad_timezone');
    if (stored) {
      cachedTimezone = stored;
      tzLoaded = true;
    }
  }
}

function ensureUtc(date: string | Date): Date {
  if (typeof date === 'string') {
    // Backend sends naive ISO strings (no timezone). Treat them as UTC.
    if (!date.endsWith('Z') && !date.includes('+') && !date.includes('-', 10)) {
      return new Date(date + 'Z');
    }
    return new Date(date);
  }
  return date;
}

export function formatTime24(date: string | Date): string {
  const d = ensureUtc(date);
  try {
    return d.toLocaleTimeString('en-GB', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZone: cachedTimezone,
    });
  } catch {
    return d.toLocaleTimeString('en-GB', { hour12: false });
  }
}

export function formatDateShort(date: string | Date): string {
  const d = ensureUtc(date);
  try {
    const opts: Intl.DateTimeFormatOptions = { timeZone: cachedTimezone, year: '2-digit', month: '2-digit', day: '2-digit' };
    return d.toLocaleDateString('en-US', opts);
  } catch {
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const yy = String(d.getFullYear()).slice(-2);
    return `${mm}/${dd}/${yy}`;
  }
}

function zoneParts(d: Date, tz: string): Record<string, number> {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz, hourCycle: 'h23',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).formatToParts(d);
  const out: Record<string, number> = {};
  parts.forEach((p) => { if (p.type !== 'literal') out[p.type] = Number(p.value); });
  return out;
}

/** A stored (UTC) timestamp as a `datetime-local` value in the tenant's timezone. */
export function toLocalInputValue(date: string | Date): string {
  const d = ensureUtc(date);
  try {
    const p = zoneParts(d, cachedTimezone);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${p.year}-${pad(p.month)}-${pad(p.day)}T${pad(p.hour)}:${pad(p.minute)}:${pad(p.second)}`;
  } catch {
    return d.toISOString().slice(0, 19);
  }
}

/** A `datetime-local` value entered in the tenant's timezone, as a UTC ISO string. */
export function localInputToUtcIso(value: string): string {
  const [datePart, timePart = '00:00'] = value.split('T');
  const [y, mo, d] = datePart.split('-').map(Number);
  const [h, mi, s = 0] = timePart.split(':').map(Number);
  const wall = Date.UTC(y, mo - 1, d, h, mi, s);
  let guess = wall;
  // Two passes settle the offset across a DST boundary.
  for (let i = 0; i < 2; i += 1) {
    const p = zoneParts(new Date(guess), cachedTimezone);
    const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
    guess = wall - (asUtc - guess);
  }
  return new Date(guess).toISOString();
}

export function formatDateTime24(date: string | Date): string {
  const d = ensureUtc(date);
  try {
    return d.toLocaleString('en-US', {
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZone: cachedTimezone,
    });
  } catch {
    return d.toLocaleString('en-US', { hour12: false });
  }
}
